import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as PresentationServiceModule from '../services/PresentationService';
const presentationService = PresentationServiceModule.default;
type Presentation = PresentationServiceModule.Presentation;

const Dashboard: React.FC = () => {
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    
    const fetchPresentations = async () => {
      try {
        setLoading(true);
        const userPresentations = await presentationService.getUserPresentations();
        setPresentations(userPresentations);
        setError(null);
      } catch (err) {
        console.error('Error fetching presentations:', err);
        setError('Failed to load your presentations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPresentations();
  }, [currentUser]);

  const handleViewPresentation = (id: string) => {
    navigate(`/presentation/${id}`);
  };

  const handleEditPresentation = (id: string) => {
    navigate(`/edit/${id}`);
  };

  const handleDeletePresentation = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this presentation?')) {
      try {
        await presentationService.deletePresentation(id);
        setPresentations(presentations.filter(p => p.id !== id));
      } catch (err) {
        console.error('Error deleting presentation:', err);
        setError('Failed to delete presentation. Please try again later.');
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/');
  };

  // Format date for display
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">My Presentations</h1>
        <button
          onClick={handleCreateNew}
          className="px-4 py-2 bg-gradient-to-br from-violet-600 to-violet-800 text-white rounded-lg hover:from-violet-500 hover:to-violet-700 transition-all duration-300 flex items-center shadow-lg shadow-violet-900/30"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Create New Presentation
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-violet-600 to-teal-600 rounded-full opacity-70 blur-lg animate-pulse"></div>
            <div className="relative w-12 h-12 border-4 border-t-transparent border-teal-400 rounded-full animate-spin"></div>
          </div>
        </div>
      ) : presentations.length === 0 ? (
        <div className="glassmorphism rounded-xl p-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-gray-800/80 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">No presentations yet</h2>
          <p className="text-gray-400 mb-6">Create your first presentation to get started</p>
          <button
            onClick={handleCreateNew}
            className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-lg hover:from-violet-500 hover:to-purple-600 transition-all duration-300 inline-flex items-center shadow-lg shadow-violet-900/30"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Create Presentation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {presentations.map((presentation) => (
            <div 
              key={presentation.id} 
              className="glassmorphism rounded-xl overflow-hidden border border-white/10 hover:border-teal-500/30 transition-all duration-300 group"
            >
              <div 
                className="h-40 bg-gradient-to-br from-gray-800 to-gray-900 relative"
                onClick={() => handleViewPresentation(presentation.id!)}
              >
                {/* Preview thumbnail */}
                <div className="absolute inset-0 flex items-center justify-center p-4 text-white/70">
                  <div className="w-full max-w-[200px] aspect-video bg-white/10 rounded-lg backdrop-blur-sm border border-white/20 flex items-center justify-center">
                    <span className="text-lg font-medium truncate px-4">{presentation.title}</span>
                  </div>
                </div>
                
                {/* Quick actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPresentation(presentation.id!);
                      }}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-colors duration-200"
                      title="View presentation"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditPresentation(presentation.id!);
                      }}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-colors duration-200"
                      title="Edit presentation"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePresentation(presentation.id!);
                      }}
                      className="p-2 rounded-lg bg-white/10 hover:bg-red-600/80 text-white backdrop-blur-sm transition-colors duration-200"
                      title="Delete presentation"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <div 
                className="p-4 cursor-pointer"
                onClick={() => handleViewPresentation(presentation.id!)}
              >
                <h3 className="text-xl font-semibold text-white mb-2 truncate group-hover:text-teal-300 transition-colors duration-300">
                  {presentation.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {presentation.content.substring(0, 120)}...
                </p>
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <div>
                    {presentation.contextFiles && presentation.contextFiles.length > 0 && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                        </svg>
                        {presentation.contextFiles.length} {presentation.contextFiles.length === 1 ? 'file' : 'files'}
                      </span>
                    )}
                  </div>
                  <span>Updated {formatDate(presentation.updatedAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard; 